<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useFormValidation } from "../../composables/useFormValidation";
import MessageBoxService from "../../services/MessageBox";
import Toast from "../../services/Toast";
import { api } from "../../api/Api";
import { required } from "../../utils/validation";
import FileUpload from "../../components/ui/form/FileUpload.vue";
import TextInput from "../../components/ui/form/TextInput.vue";
import TextAreaInput from "../../components/ui/form/TextAreaInput.vue";
import GroupedSelect from "../../components/ui/form/GroupedSelect.vue";
import NumberInput from "../../components/ui/form/NumberInput.vue";
import PhoneInput from "../../components/ui/form/PhoneInput.vue";
import IconComponent from "../../components/ui/common/IconComponent.vue";

const endPoint = "/clients/system/company/company-information/";
const title = "Company Information";

const emit = defineEmits<{
  (e: "save"): void;
  (e: "skip"): void;
}>();

const { fields, formData, setFieldRules, validateForm, setFormData } = useFormValidation({
  logo: "",
  name: "",
  tax_identification_no: "",
  prefix: "",
  suffix: "",
  address: "",
  buildings_id: "",
  lng: "",
  lat: "",
  tel: "",
  mobile: "",
  viber: "",
  whatsapp: "",
  email: "",
  url: "",
  letterhead: "",
  active: true,
  uid: "",
  footer_text: "",
});

setFieldRules("name", [required("Name is required")]);
setFieldRules('logo', [required('Logo is required')])
setFieldRules("mobile", [required("Mobile is required")]);
setFieldRules("email", [required("Email is required")]);

const handleSubmit = async () => {
    if (!formData.value.buildings_id && !formData.value.address) {
        MessageBoxService.show('Select the Building or Enter Address to proceed')
        return
    }
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save");
    }
  }
};

const isLoading = ref(false);
const handleDataLoad = async () => {
  const apiData = {};
  isLoading.value = true;
  const response = await api.post(endPoint + "get", apiData, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    skipLoading: true,
  });
  if (response.success == true) {
    isLoading.value = false;
    setFormData(response.data);
  } else {
    isLoading.value = false;
    MessageBoxService.show("Could not load data", "warning");
  }
};

onMounted(async () => {
  handleDataLoad();
});
</script>

<template>
  <div
    class="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 w-full self-start relative mb-[200px]"
  >
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-xl font-semibold">{{ title }}</h3>
    <button
    @click="handleSubmit"
      class="px-2 md:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center"
    >
      <IconComponent name="CircleArrowRight" />
      <span class="ml-2 uppercase tracking-wide">Next</span>
    </button>
    </div>
    <div
      v-if="isLoading"
      class="absolute top-0 left-0 w-full h-full bg-blue-500 bg-opacity-50 flex items-center justify-center animate-blink"
    >
      <div
        class="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 animate-spin"
      ></div>
    </div>
    <div v-else>
    <div id="document-section">
      <FileUpload
        accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
        label="Logo"
        :max-size="5"
        :multiple="false"
        :image="true"
        path="company_info"
        v-model="fields.logo.value"
        :class="{'border-red-500': fields.logo.touched && fields.logo.error}"
      />
      <p v-if="fields.logo.touched && fields.logo.error" class="text-red-500 text-sm mt-1">
        {{ fields.logo.error }}
      </p>
    </div>
      <div class="grid md:grid-cols-2 gap-6">
        <TextInput
          v-model="fields.name.value"
          label="Name"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
        />

        <TextInput
          v-model="fields.tax_identification_no.value"
          label="TAX Identification No"
          :error="
            fields.tax_identification_no.touched
              ? fields.tax_identification_no.error
              : null
          "
          @blur="fields.tax_identification_no.touched = true"
        />

        <TextInput
          v-model="fields.prefix.value"
          label="Prefix"
          :error="fields.prefix.touched ? fields.prefix.error : null"
          @blur="fields.prefix.touched = true"
        />

        <TextInput
          v-model="fields.suffix.value"
          label="Suffix"
          :error="fields.suffix.touched ? fields.suffix.error : null"
          @blur="fields.suffix.touched = true"
        />

        <TextAreaInput
          v-model="fields.address.value"
          :rows="4"
          label="Address"
          :error="fields.address.touched ? fields.address.error : null"
          @blur="fields.address.touched = true"
        />

        <GroupedSelect
          v-model="fields.buildings_id.value"
          label="Building"
          end-point="system/master-data/buildings"
          optionLabel="buildings"
          :error="
            fields.buildings_id.touched ? fields.buildings_id.error : null
          "
          @blur="fields.buildings_id.touched = true"
        />

        <NumberInput
          v-model="fields.lat.value"
          label="Latitude"
          :min="0"
          :error="fields.lat.touched ? fields.lat.error : null"
          @blur="fields.lat.touched = true"
        />

        <NumberInput
          v-model="fields.lng.value"
          label="Longitude"
          :min="0"
          :error="fields.lng.touched ? fields.lng.error : null"
          @blur="fields.lng.touched = true"
        />

        <PhoneInput
          v-model="fields.tel.value"
          label="Phone"
          :error="fields.tel.touched ? fields.tel.error : null"
          @blur="fields.tel.touched = true"
        />

        <PhoneInput
          v-model="fields.mobile.value"
          label="Mobile"
          :error="fields.mobile.touched ? fields.mobile.error : null"
          @blur="fields.mobile.touched = true"
        />

        <PhoneInput
          v-model="fields.viber.value"
          label="Viber"
          :error="fields.viber.touched ? fields.viber.error : null"
          @blur="fields.viber.touched = true"
        />

        <PhoneInput
          v-model="fields.whatsapp.value"
          label="WhatsApp"
          :error="fields.whatsapp.touched ? fields.whatsapp.error : null"
          @blur="fields.whatsapp.touched = true"
        />

        <TextInput
          v-model="fields.email.value"
          label="Email"
          :error="fields.email.touched ? fields.email.error : null"
          @blur="fields.email.touched = true"
        />

        <TextInput
          v-model="fields.url.value"
          label="URL"
          :error="fields.url.touched ? fields.url.error : null"
          @blur="fields.url.touched = true"
        />

        <TextAreaInput
          v-model="fields.footer_text.value"
          label="Document Footer Text"
          :error="fields.footer_text.touched ? fields.footer_text.error : null"
          @blur="fields.footer_text.touched = true"
        />

      </div>
    </div>
  </div>
</template>
